import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableVirtuoso, Virtuoso } from "react-virtuoso";
import { BrandTableHeaderCell } from "./table_header_cell";
import { BrandTableBodyCell } from "./table_body_cell";
import { Skeleton, TableCell, useMediaQuery, useTheme } from "@mui/material";
import { BrandLoadingState } from "../loader/loading_state";
import "./virtualised_table.scss";
import { BrandTableListItem } from "./list_item";
import { Typography } from "components/Common/Typography/Typography";
import { colors } from "assets/colors/colors";

const BrandTableRow = React.forwardRef((props, ref) => {
  const { context, item, style, ...rest } = props;
  const { onRowPressed, setHoveredRow, rowHeight } = context || {};

  return (
    <TableRow
      {...rest}
      ref={ref}
      style={style}
      onMouseEnter={onRowPressed ? () => setHoveredRow(item) : undefined}
      onMouseLeave={onRowPressed ? () => setHoveredRow(null) : undefined}
      hover={!!onRowPressed}
      onClick={() => onRowPressed?.(item)}
      sx={{
        ...(onRowPressed ? { cursor: "pointer" } : undefined),
        ...(rowHeight
          ? {
              height: `${rowHeight}px !important`,
              "& .MuiTableCell-root": {
                height: `${rowHeight}px !important`,
                py: 0,
              },
            }
          : undefined),
      }}
    />
  );
});

const Components = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      {...props}
      sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
    />
  ),
  TableHead: React.forwardRef((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow: BrandTableRow,
  TableBody: React.forwardRef((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
  TableFoot: React.forwardRef(({ children, context }, ref) => {
    const columnCount = context?.columnCount || 1;
    return (
      <tfoot ref={ref}>
        <TableRow>
          <TableCell
            colSpan={columnCount}
            align="center"
            sx={{ padding: "24px" }}
          >
            {children}
          </TableCell>
        </TableRow>
      </tfoot>
    );
  }),
  EmptyPlaceholder: ({ context }) => (
    <TableRow>
      <td
        colSpan={context?.columnCount ?? 99}
        align="center"
        style={{ padding: "24px" }}
      >
        <Typography
          variant="h2_5"
          color={colors.core70}
          text="No Data Found."
        />
      </td>
    </TableRow>
  ),
};

export const BRAND_TABLE_ACTIONS_COLUMN_DATA_KEY = "actions";
export const BrandVirtualisedTable = ({
  columns,
  data,
  onLoadMore,
  onRowPressed,
  formControl,
  formFieldName,
  rowHeight,
}) => {
  const [hoveredRow, setHoveredRow] = React.useState(null);

  let isSmallScreen = useMediaQuery(useTheme().breakpoints.up("sm"));

  return (
    <div className="brand-virtualised-table">
      {!data ? (
        <div className="brand-virtualised-table_loading">
          {Array.from({ length: 20 }).map((_, index) =>
            index % 2 === 0 ? (
              <div key={index} style={{ height: 48 }} />
            ) : (
              <Skeleton key={index} variant="rectangular" height={48} />
            ),
          )}
        </div>
      ) : isSmallScreen ? (
        <TableVirtuoso
          data={data?.results || []}
          endReached={onLoadMore}
          increaseViewportBy={100}
          fixedItemHeight={rowHeight}
          defaultItemHeight={rowHeight}
          context={{
            onRowPressed,
            setHoveredRow,
            columnCount: columns.length,
            rowHeight,
          }}
          components={Components}
          fixedHeaderContent={() => (
            <TableRow>
              {columns.map((column) => (
                <BrandTableHeaderCell key={column.dataKey} column={column} />
              ))}
            </TableRow>
          )}
          fixedFooterContent={data?.next ? _LoadingFooter : undefined}
          itemContent={(_index, row) => {
            return (
              <>
                {columns.map((column) => {
                  if (column.dataKey === BRAND_TABLE_ACTIONS_COLUMN_DATA_KEY) {
                    return (
                      <TableCell
                        key={BRAND_TABLE_ACTIONS_COLUMN_DATA_KEY}
                        align={column.align || "right"}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={
                          rowHeight
                            ? {
                                height: `${rowHeight}px`,
                                py: 0,
                              }
                            : undefined
                        }
                      >
                        {column.children({
                          rowData: row,
                          isWideScreen: isSmallScreen,
                          isHovered: hoveredRow?.uuid === row.uuid,
                        })}
                      </TableCell>
                    );
                  }
                  return (
                    <BrandTableBodyCell
                      key={column.dataKey}
                      column={column}
                      row={row}
                      formControl={formControl}
                      formFieldName={formFieldName}
                      rowHeight={rowHeight}
                    />
                  );
                })}
              </>
            );
          }}
        />
      ) : (
        <Virtuoso
          data={data?.results || []}
          endReached={onLoadMore}
          increaseViewportBy={100}
          fixedItemHeight={rowHeight}
          defaultItemHeight={rowHeight}
          context={{ columnCount: columns.length }}
          components={{
            EmptyPlaceholder: () => (
              <div style={{ textAlign: "center", padding: "24px" }}>
                <Typography
                  variant="h2_5"
                  color={colors.core70}
                  text="No Data Found."
                />
              </div>
            ),
            Footer: data?.next ? _LoadingFooter : undefined,
          }}
          itemContent={(index, row) => (
            <BrandTableListItem
              key={row.uuid}
              rowData={row}
              columns={columns}
              formControl={formControl}
              formFieldName={formFieldName}
              index={index}
            />
          )}
        />
      )}
    </div>
  );
};

const _LoadingFooter = () => {
  return (
    <div className="brand-virtualised-table__loading-footer">
      <BrandLoadingState size={24} />
    </div>
  );
};
